import { QRCodeSVG } from 'qrcode.react';
import type { AnimationEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes, useTheme } from 'styled-components';

import LogoMarkBadgeSvg from '../../../assets/logos/logo-mark-badge.svg?react';
import { zIndexMap } from '../../../shared/config';
import { useSheetPresence } from '../../../shared/hooks/use-sheet-presence';
import { PrimaryButton, rubikTypographyStyles } from '../../../shared/ui';

const REFERRAL_BONUS_SHEET_CLOSE_ANIMATION_MS = 240;
const QR_CODE_SIZE_PX = 218;

const overlayFadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const overlayFadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const sheetSlideIn = keyframes`
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
`;

const sheetSlideOut = keyframes`
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
`;

const getOverlayAnimationCss = (isClosing: boolean) =>
  isClosing
    ? css`
        animation: ${overlayFadeOut}
          ${REFERRAL_BONUS_SHEET_CLOSE_ANIMATION_MS}ms ease-in both;
      `
    : css`
        animation: ${overlayFadeIn} 300ms ease-out both;
      `;

const getSheetAnimationCss = (isClosing: boolean) =>
  isClosing
    ? css`
        animation: ${sheetSlideOut} ${REFERRAL_BONUS_SHEET_CLOSE_ANIMATION_MS}ms
          ease-in both;
      `
    : css`
        animation: ${sheetSlideIn} 360ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
      `;

const Overlay = styled.div.attrs({
  'data-ui': 'referral-bonus-qr-sheet-overlay',
})<{ $isClosing: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(65)}, env(safe-area-inset-top))`};
  background: ${({ theme }) => theme.colors.surface.backdrop};
  overscroll-behavior: contain;
  touch-action: pan-y;
  z-index: ${zIndexMap.referralBonus.overlay};
  ${({ $isClosing }) => getOverlayAnimationCss($isClosing)};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Sheet = styled.section.attrs({
  'aria-labelledby': 'referral-bonus-qr-sheet-title',
  'aria-modal': 'true',
  'data-ui': 'referral-bonus-qr-sheet',
  role: 'dialog',
})<{ $isClosing: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top-left-radius: ${({ theme }) => theme.scale.px(21)};
  border-top-right-radius: ${({ theme }) => theme.scale.px(21)};
  padding: ${({ theme }) => theme.scale.px(28)}
    ${({ theme }) => theme.scale.px(31)}
    ${({ theme }) => `max(${theme.scale.px(40)}, env(safe-area-inset-bottom))`};
  background: ${({ theme }) => theme.colors.surface.base};
  z-index: ${zIndexMap.referralBonus.sheet};
  ${({ $isClosing }) => getSheetAnimationCss($isClosing)};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Title = styled.h2`
  ${rubikTypographyStyles};
  width: 100%;
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(27)};
  white-space: pre-line;
`;

const Description = styled.p`
  width: 100%;
  margin: ${({ theme }) => theme.scale.px(11)} 0 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(15)};
`;

const QrSurface = styled.div.attrs({
  'aria-hidden': true,
  'data-ui': 'referral-bonus-qr-sheet-code',
})`
  width: ${({ theme }) => theme.scale.px(QR_CODE_SIZE_PX)};
  height: ${({ theme }) => theme.scale.px(QR_CODE_SIZE_PX)};
  position: relative;
  display: grid;
  place-items: center;
  margin-top: ${({ theme }) => theme.scale.px(15)};
  overflow: hidden;
  border-radius: ${({ theme }) => theme.scale.px(8)};
  background: ${({ theme }) => theme.colors.surface.base};

  & > svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const LogoBadgeShell = styled.div`
  width: ${({ theme }) => theme.scale.px(68)};
  height: ${({ theme }) => theme.scale.px(68)};
  position: absolute;
  top: 50%;
  left: 50%;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.scale.px(8)};
  background: ${({ theme }) => theme.colors.surface.base};
  pointer-events: none;
  transform: translate(-50%, -50%);
`;

const LogoBadge = styled(LogoMarkBadgeSvg).attrs({
  'aria-hidden': true,
})`
  width: ${({ theme }) => theme.scale.px(54)};
  height: ${({ theme }) => theme.scale.px(54)};
  display: block;
`;

const ShareButton = styled(PrimaryButton)`
  margin-top: ${({ theme }) => theme.scale.px(27)};

  &:disabled {
    opacity: 0.4;
  }
`;

type ReferralBonusQrSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  onShare: () => Promise<void> | void;
  referralLink: string | null;
};

function ReferralBonusQrCode({ referralLink }: { referralLink: string }) {
  const theme = useTheme();

  return (
    <QrSurface>
      <QRCodeSVG
        bgColor={theme.colors.surface.base}
        fgColor={theme.colors.text.primary}
        includeMargin
        level="H"
        size={theme.scale.value(QR_CODE_SIZE_PX)}
        value={referralLink}
      />
      <LogoBadgeShell>
        <LogoBadge />
      </LogoBadgeShell>
    </QrSurface>
  );
}

export function ReferralBonusQrSheet({
  isOpen,
  onClose,
  onShare,
  referralLink,
}: ReferralBonusQrSheetProps) {
  const { t } = useTranslation();
  const { handleCloseAnimationEnd, isClosing, isMounted } =
    useSheetPresence(isOpen);

  const stopOverlayClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const handleSheetAnimationEnd = (event: AnimationEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget || !isClosing) {
      return;
    }

    handleCloseAnimationEnd();
  };

  if (!isMounted) {
    return null;
  }

  return (
    <Overlay $isClosing={isClosing} onClick={onClose}>
      <Sheet
        $isClosing={isClosing}
        onAnimationEnd={handleSheetAnimationEnd}
        onClick={stopOverlayClose}
      >
        <Title id="referral-bonus-qr-sheet-title">
          {t('referralBonus.qrSheet.title')}
        </Title>
        <Description>{t('referralBonus.qrSheet.description')}</Description>
        {referralLink ? (
          <ReferralBonusQrCode referralLink={referralLink} />
        ) : null}
        <ShareButton
          type="button"
          onClick={() => {
            void onShare();
          }}
          disabled={!referralLink}
        >
          {t('referralBonus.qrSheet.shareCta')}
        </ShareButton>
      </Sheet>
    </Overlay>
  );
}
